
import React, { useEffect, useState, useMemo } from "react";
import { useCart } from "./CartContext";
import { Link } from "react-router-dom";
import { items as catalogItems } from "../mockData/goods-data";
import "./cart.scss";

export default function CartModal() {
    const { items, totalQty, totalSum, inc, dec, remove, add } = useCart();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onOpen = () => setOpen(true);
        const onClose = () => setOpen(false);
        window.addEventListener("cart:open", onOpen);
        window.addEventListener("cart:close", onClose);
        return () => {
            window.removeEventListener("cart:open", onOpen);
            window.removeEventListener("cart:close", onClose);
        };
    }, []);

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.body.style.overflow = open ? "hidden" : "";
        if (open) window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", onKey);
        };
    }, [open]);

    // --- helpers ---
    const parsePrice = (str) => Number(String(str).replace(/[^\d]/g, "")) || 0;

    // Унікальні SKU в кошику (щоб не рекомендувати те саме)
    const cartSkus = useMemo(() => new Set(items.map(i => i.sku)), [items]);

    // Формуємо список рекомендацій (до 10 шт), виключивши те, що вже в кошику
    const recs = useMemo(() => {
        return catalogItems
            .filter(g =>
                !cartSkus.has(g.skuFirst) &&
                !cartSkus.has(g.skuSecond) &&
                !cartSkus.has(g.skuThird) &&
                !cartSkus.has(g.skuFourth)
            )
            .slice(0, 10)
            // мапимо в “мінімальний SKU” для додавання одразу до кошика
            .map(g => ({
                title: g.title,
                sku: g.skuThird || g.skuFirst || g.skuSecond || g.skuFourth, // дефолтний вибір
                unit: g.unitThird || g.unitFirst || g.unitSecond || g.unitFourth || "",
                price: parsePrice(g.priceThird || g.priceFirst || g.priceSecond || g.priceFourth),
                img: g.imgThird, // у масиві є саме URL/імпорт
                inside: g.inside,
            }));
    }, [catalogItems, cartSkus]);

    const addRecToCart = (r) => {
        add({
            sku: r.sku,
            title: r.title,
            price: r.price,
            img: r.img,
            unit: r.unit,
            quantity: 1,
        });
    };

    if (!open) return null;

    return (
        <div className="modal cartModal" role="dialog" aria-modal="true" aria-label="Кошик">
            <div className="modal__backdrop" onClick={() => setOpen(false)} />
            <div className="cartModal__card">
                <button className="modal__close" onClick={() => setOpen(false)} aria-label="Закрити">✕</button>

                <header className="cartModal__header">
                    <h2 className="cartModal__title">Кошик</h2>
                    <div className="cartModal__colsHead">
                        <span>Кількість</span>
                        <span>Вартість</span>
                    </div>
                </header>

                {/* Товари */}
                <ul className="cartModal__list">
                    {items.map((i) => (
                        <li key={i.sku} className="cartModal__row">
                            {(() => {
                                const src = typeof i.img === "string" ? i.img : (i.img?.props?.src || "/placeholder.jpg");
                                return <img src={src} alt={i.title} className="cartModal__img" />;
                            })()}
                            <div className="cartModal__info">
                                <div className="cartModal__name">{i.title}{i.unit ? `, ${i.unit}` : ""}</div>
                                <small className="summary__sku">Артикул: {i.sku}</small>
                            </div>

                            <div className="cartModal__qty">
                                <button onClick={() => dec(i.sku)} aria-label="Зменшити">−</button>
                                <span>{i.quantity}</span>
                                <button onClick={() => inc(i.sku)} aria-label="Збільшити">+</button>
                            </div>

                            <div className="cartModal__sum">{(i.price * i.quantity).toLocaleString("uk-UA")} грн</div>
                            <button className="cartModal__remove" onClick={() => remove(i.sku)} aria-label="Видалити">🗑️</button>
                        </li>
                    ))}
                </ul>
                <footer className="cartModal__footer">
                    <button className="cartModal__back" onClick={() => setOpen(false)}>← Повернутись до покупок</button>
                    <div className="cartModal__total">
                        <span>Всього:</span>
                        <b>{totalSum.toLocaleString("uk-UA")} грн</b>
                    </div>
                    <Link to="/checkout" className="btn-primary" onClick={() => setOpen(false)}>
                        Оформити замовлення
                    </Link>
                </footer>

                {/* === Рекомендації === */}
                {recs.length > 0 && (
                    <section className="cartModal__recs">
                        <h3>Рекомендуємо придбати</h3>
                        <div className="cartModal__recsList">
                            {recs.map(r => (
                                <div key={r.sku} className="recCard">
                                    <Link to={`/${r.inside}`} onClick={() => setOpen(false)} className="recCard__imgWrap">
                                        <img src={r.img} alt={r.title} />
                                    </Link>
                                    <Link to={`/${r.inside}`} onClick={() => setOpen(false)} className="recCard__title">
                                        {r.title}
                                    </Link>
                                    <div className="recCard__price">{r.price.toLocaleString("uk-UA")} грн</div>
                                    <button className="recCard__btn" onClick={() => addRecToCart(r)}>До кошика</button>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}
